import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { http } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead, Btn, Figure } from "../components/Bits";
import { QuoteForm } from "../components/QuoteForm";

const SPEC_LABELS = {
  thickness: "Thickness",
  dimensions: "Dimensions",
  density: "Density",
  surface_finish: "Surface finish",
  colours: "Available colours",
  weight: "Weight",
  installation_method: "Installation method",
  warranty: "Warranty",
  certifications: "Certifications",
  lead_time: "Lead time",
};

export const ProductTemplate = ({ product }) => {
  const { products } = useSite();
  const related = products.filter((p) => p.slug !== product.slug).slice(0, 3);

  return (
    <div data-testid={`product-page-${product.slug}`}>
      <PageHero
        eyebrow={product.label}
        title={product.name}
        copy={product.value_prop}
        image={product.hero_image}
        ctas={[
          { label: "Request a quote", to: "/contact", testId: "product-quote-cta" },
          {
            label: "View installations",
            to: "/installations",
            variant: "outline",
            testId: "product-installations-cta",
          },
        ]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
          <Reveal className="lg:col-span-7">
            <SectionHead eyebrow="Overview" title={product.tagline} />
            <p className="mt-8 text-sm leading-relaxed text-[#3B3B3B] sm:text-base">
              {product.overview}
            </p>
            <div className="mt-12 grid gap-10 sm:grid-cols-2">
              <div>
                <p className="mako-label text-[#7A7F85]">Best suited for</p>
                <ul className="mt-5 space-y-3">
                  {(product.best_for || []).map((b) => (
                    <li key={b} className="flex gap-3 text-sm text-[#3B3B3B]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C81]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mako-label text-[#7A7F85]">Surface and finish options</p>
                <ul className="mt-5 space-y-3">
                  {(product.surface_options || []).map((s) => (
                    <li key={s} className="border-l-2 border-[#0F4C81] pl-4 text-sm text-[#3B3B3B]">
                      {s}
                    </li>
                  ))}
                </ul>
                <p className="mako-label mt-9 text-[#7A7F85]">Installation overview</p>
                <p className="mt-4 text-sm leading-relaxed text-[#3B3B3B]">{product.installation}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="border border-[#111111]/10 bg-[#F5F5F2] p-8 lg:p-10">
              <p className="mako-label text-[#0F4C81]">Specification</p>
              <p className="mt-4 text-xs leading-relaxed text-[#7A7F85]">
                Specifications are confirmed against the configuration selected for your facility.
              </p>
              <dl className="mt-8 divide-y divide-[#111111]/10" data-testid="product-specs">
                {Object.entries(SPEC_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-baseline justify-between gap-6 py-3.5">
                    <dt className="mako-label text-[#111111]">{label}</dt>
                    <dd className="text-right text-xs text-[#7A7F85]">
                      {product.specs?.[key] || "To be confirmed"}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead eyebrow="Gallery" title="Surface and installation detail" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(product.gallery || []).map((src, i) => (
              <Reveal key={src} delay={i * 0.06}>
                <Figure src={src} alt={`${product.name} detail ${i + 1}`} ratio="aspect-[4/5]" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Customisation" title="Available customisation" />
            <ul className="mt-8 space-y-3">
              {(product.customisation || []).map((c) => (
                <li key={c} className="flex gap-3 text-sm text-[#3B3B3B]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C81]" />
                  {c}
                </li>
              ))}
            </ul>
            <p className="mako-label mt-10 text-[#7A7F85]">Recommended disciplines</p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {(product.disciplines || []).map((d) => (
                <span
                  key={d}
                  className="border border-[#111111]/15 px-3.5 py-2 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[#3B3B3B]"
                >
                  {d}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <SectionHead eyebrow="FAQs" title="Questions we are asked" />
            <Accordion type="single" collapsible className="mt-8" data-testid="product-faqs">
              {(product.faqs || []).map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-[#111111]/10">
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-[#111111] hover:text-[#0F4C81] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-[#3B3B3B]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="border-t border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead eyebrow="Related" title="Other MAKO systems" />
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={r.type === "wall" ? "/wall-pads" : `/mats/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="group border border-[#111111]/10 bg-white transition-colors duration-500 hover:border-[#0F4C81]/40"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={r.card_image || r.hero_image}
                    alt={r.short_name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-6">
                  <p className="mako-label text-[#0F4C81]">{r.label}</p>
                  <p className="mt-3 font-display text-lg font-bold uppercase tracking-tight text-[#111111]">
                    {r.short_name}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="quote" className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
          <div className="lg:col-span-4">
            <SectionHead
              eyebrow="Request a quote"
              title={`Enquire about ${product.name}`}
              copy="Send us the room dimensions and how the space is used. We will come back with suitable options."
            />
          </div>
          <div className="lg:col-span-8">
            <QuoteForm source={`product:${product.slug}`} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setProduct(null);
    setMissing(false);
    http
      .get(`/content/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  useSeo({
    title: product?.seo?.title || "MAKO Mats",
    description: product?.seo?.description,
  });

  if (missing)
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-32 lg:px-10">
        <p className="mako-label text-[#0F4C81]">Not found</p>
        <h1 className="mt-6 font-display text-3xl font-bold uppercase text-[#111111]">
          That product does not exist
        </h1>
        <div className="mt-10">
          <Btn to="/mats">Back to combat mats</Btn>
        </div>
      </div>
    );

  if (!product) return <div className="min-h-[60vh]" data-testid="product-loading" />;

  return <ProductTemplate product={product} />;
}
